import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useUpdateChannel, type Channel } from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { PlayoutModeField } from './PlayoutModeField';
import { resolvePlayoutMode, playoutModeToInput, type PlayoutMode } from './playout-modes';

interface EditChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  channel: Channel;
}

export function EditChannelDialog({ open, onOpenChange, serviceUuid, channel }: EditChannelDialogProps) {
  const { show } = useToast();
  const [title, setTitle] = useState(channel.title ?? '');
  const [description, setDescription] = useState(channel.description ?? '');
  const [mode, setMode] = useState<PlayoutMode>(() => resolvePlayoutMode(channel));

  const update = useUpdateChannel(serviceUuid, channel.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Channel updated' });
      trackFeatureAction('fastchannel', 'channel', 'edit');
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update channel' }),
  });

  const valid = title.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit channel</DialogTitle>
          <DialogDescription>
            Update the channel details. The key (<code>{channel.name}</code>) can't be changed.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Title" required>
              {(props) => (
                <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />
              )}
            </Field>
            <Field label="Description">
              {(props) => (
                <Textarea
                  {...props}
                  value={description}
                  rows={2}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>
            <PlayoutModeField value={mode} onChange={setMode} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || update.isPending}
            onClick={() =>
              update.mutate({
                title: title.trim(),
                description: description.trim(),
                ...playoutModeToInput(mode),
              })
            }
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
